import { QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState, type CSSProperties } from "react";
import { RouterProvider } from "react-router";
import { Toaster } from "sonner";
import { setUnauthorizedHandler } from "@/shared/api/client";
import { createQueryClient } from "@/shared/api/query-client";
import { ROUTES } from "@/shared/constants/routes";
import { useTheme } from "@/shared/hooks/useTheme";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { createAppRouter } from "@/routes";

/** sonner styled through our tokens (CSS variables it reads), never its default palette. */
const TOAST_STYLE = {
  "--normal-bg": "var(--surface-raised)",
  "--normal-text": "var(--text)",
  "--normal-border": "var(--border)",
  "--border-radius": "var(--radius)",
  "--font-family": "var(--font-sans)",
} as CSSProperties;

function AppToaster() {
  const { resolved } = useTheme();
  return <Toaster theme={resolved} position="bottom-right" closeButton style={TOAST_STYLE} />;
}

export function App() {
  const [queryClient] = useState(createQueryClient);
  const [router] = useState(createAppRouter);

  useEffect(() => {
    // A terminal 401 anywhere: drop every cached row, return to /login once (the client guards against loops).
    setUnauthorizedHandler(() => {
      queryClient.clear();
      void router.navigate(ROUTES.login, { replace: true, state: { from: window.location.pathname } });
    });
    return () => setUnauthorizedHandler(null);
  }, [queryClient, router]);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <RouterProvider router={router} />
        <AppToaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
